import { type ShiftAuction } from "@clipboard-health/contract-worker-app-bff";
import { useWorkerPreferences } from "@src/appV2/Agents/api/useWorkerPreferences";
import { useWorkerCustomToBaseQualificationNameMap } from "@src/appV2/Qualifications/useWorkerCustomToBaseQualificationNameMap";
import { useIsShiftBlocksEnabled } from "@src/appV2/redesign/Shift/Block/useIsShiftBlocksEnabled";
import { useMemo } from "react";

import { useShiftBundles } from "../../Shift/Bundle/useShiftBundles";
import { useShiftBundlesConfig } from "../../Shift/Bundle/useShiftBundlesConfig";
import { useShiftDoubles } from "../../Shift/Double/useShiftDoubles";
import { useShiftDoublesEnabled } from "../../Shift/Double/useShiftDoublesEnabled";
import { isPriorityShift } from "../../Shift/Priority/isPriorityShift";
import { useGetMultiLicenseAggregatedBlocks } from "../Blocks/useGetMultiLicenseAggregatedBlocks";
import { filterOpenShifts } from "../Filters/filterOpenShifts";
import { useShiftDiscoveryUserFiltersContext } from "../Filters/useUserFiltersContext";
import { useUrgentShiftsData } from "../UrgentShifts/useUrgentShiftsData";
import { useListViewOpenShiftsData } from "./useListViewOpenShiftsData";
import { useShiftListInjector } from "./useShiftListInjector";

export function useFilteredListViewOpenShiftsData() {
  const isShiftBlocksEnabled = useIsShiftBlocksEnabled();
  const shiftBundlesConfig = useShiftBundlesConfig();
  const isShiftDoublesEnabled = useShiftDoublesEnabled();

  const {
    distance,
    // Filtering by timeslot happens at the list rendering level
    // because each date can have a different set of timeslots
    shiftTimeSlots: timeSlotFilters,
    includeWindowedOutShifts,
    favoriteWorkplacesOnly,
    ...localUserFilters
  } = useShiftDiscoveryUserFiltersContext();

  const { filters: shiftBlockFilters } = useWorkerPreferences();

  const {
    shifts = [],
    workplacesMap,
    rateNegotiationsMap = new Map(),
    offersMap,
    shiftAuctionsMap = new Map<string, ShiftAuction>(),
    ...openShiftsDataResult
  } = useListViewOpenShiftsData({
    distance,
    includeWindowedOutShifts,
    favoriteWorkplacesOnly,
    isShiftBundlesEnabled: shiftBundlesConfig.enabled,
    isShiftDoublesEnabled,
  });

  const eligibleWorkplaceIds = useMemo(() => {
    const eligibleIds = new Set<string>();
    if (!workplacesMap) {
      return eligibleIds;
    }

    for (const [workplaceId, workplace] of workplacesMap) {
      if (workplace.attributes.isShiftBundlesEnabled) {
        eligibleIds.add(workplaceId);
      }
    }

    return eligibleIds;
  }, [workplacesMap]);

  const bundlesByShiftId = useShiftBundles(shifts, {
    eligibleWorkplaceIds,
    offersMap,
    workplacesMap,
  });
  const doublesByShiftId = useShiftDoubles(shifts);
  // The open shifts data doesn't include all urgent shifts, so we need to fetch them separately
  const { shifts: urgentShifts } = useUrgentShiftsData();

  const { data: blockShifts } = useGetMultiLicenseAggregatedBlocks(
    {
      maxDistanceMiles: shiftBlockFilters.distance,
    },
    {
      enabled: isShiftBlocksEnabled,
    }
  );

  const priorityShifts = shifts.filter((shift) =>
    isPriorityShift({
      priorityTill: shift.attributes.priorityTill,
      window: shift.attributes.window,
    })
  );

  const customToBaseQualificationNameMap = useWorkerCustomToBaseQualificationNameMap();

  const filteredOpenShifts = filterOpenShifts(
    { shifts, workplacesMap },
    localUserFilters,
    customToBaseQualificationNameMap
  );

  // shifts post filtering injection.  I.E. in-app ads, the home health banner (once we migrate that), etc...
  const postInjectionShifts = useShiftListInjector(filteredOpenShifts);

  return {
    data: {
      filteredOpenShifts: postInjectionShifts,
      unfilteredOpenShifts: shifts,
      workplacesMap,
      rateNegotiationsMap,
      shiftAuctionsMap,
      offersMap,
      urgentShiftsCount: urgentShifts.length,
      priorityShiftsCount: priorityShifts.length,
      blockShiftsCount: blockShifts?.data.length ?? 0,
      timeSlotFilters,
      bundlesByShiftId,
      doublesByShiftId,
    },
    ...openShiftsDataResult,
  };
}
